import React, { useEffect, useMemo, useRef, useState } from 'react';

const easeOutCubic = (value) => 1 - Math.pow(1 - value, 3);

const getDecimalPlaces = (value) => {
  if (!Number.isFinite(value) || Number.isInteger(value)) return 0;

  const [, decimals = ''] = String(value).split('.');
  return Math.min(decimals.length, 2);
};

function AnimatedNumber({
  value,
  duration = 800,
  decimals,
  prefix = '',
  suffix = '',
  fallback = '0',
  formatValue
}) {
  const target = Number(value);
  const hasValidTarget = Number.isFinite(target);
  const resolvedDecimals = decimals ?? getDecimalPlaces(target);
  const [displayValue, setDisplayValue] = useState(hasValidTarget ? 0 : null);
  const latestValueRef = useRef(hasValidTarget ? 0 : null);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  useEffect(() => {
    if (!hasValidTarget) {
      latestValueRef.current = null;
      setDisplayValue(null);
      return undefined;
    }

    if (prefersReducedMotion || duration <= 0) {
      latestValueRef.current = target;
      setDisplayValue(target);
      return undefined;
    }

    const startValue = Number.isFinite(latestValueRef.current)
      ? latestValueRef.current
      : 0;
    const startTime = performance.now();
    let animationFrame;

    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const nextValue = startValue + (target - startValue) * easeOutCubic(progress);

      latestValueRef.current = nextValue;
      setDisplayValue(nextValue);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(tick);
      } else {
        latestValueRef.current = target;
        setDisplayValue(target);
      }
    };

    animationFrame = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(animationFrame);
  }, [duration, hasValidTarget, prefersReducedMotion, target]);

  if (!hasValidTarget || displayValue === null) {
    return <>{fallback}</>;
  }

  const roundedValue = Number(displayValue.toFixed(resolvedDecimals));
  const normalizedValue = Object.is(roundedValue, -0) ? 0 : roundedValue;
  const formattedValue = formatValue
    ? formatValue(normalizedValue)
    : normalizedValue.toLocaleString('it-IT', {
      minimumFractionDigits: resolvedDecimals,
      maximumFractionDigits: resolvedDecimals
    });

  return <>{prefix}{formattedValue}{suffix}</>;
}

export default AnimatedNumber;
